/**
 * Калькуляция блюд.
 *
 * Два взгляда: сводка «где мы теряем деньги» и карта отдельного блюда.
 * Сводка первой — владелец приходит не любоваться техкартами,
 * а искать блюда, которые продаются в убыток.
 *
 * Оформление 2.10: фудкост — не ярлык, а шкала с порогами 35 % и 45 %;
 * убыточная строка держит терракотовый рубец слева; три веса
 * ингредиента показаны как путь «взяли → почистили → на тарелке»,
 * а не как семь одинаковых столбцов.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const API = '/api/v1/calc';

type Verdict = 'GOOD' | 'HIGH' | 'CRITICAL' | 'UNKNOWN';

type Row = {
  productId: string; productName: string; price: number; cost: number;
  unpriced: number; foodcostPct: number | null; verdict: Verdict; markupPct: number | null;
};

type CardLine = {
  ingredientId: string; name: string; ingredientUnit: string;
  gross: number; coldLossPct: number; hotLossPct: number;
  net: number; yield: number; cost: number | null;
};

type Card = {
  productId: string; productName: string; price: number; unit: string;
  yieldGram: number | null; targetFoodcostPct: number | null;
  lines: CardLine[]; cost: number; unpriced: number; totalYield: number;
  markupPct: number | null; foodcostPct: number | null;
  verdict: Verdict; suggestedPrice: number | null;
};

const money = (v: number) => `${Math.round(v / 100).toLocaleString('ru-RU')} ₸`;
const VERDICT: Record<Verdict, { text: string; cls: string }> = {
  GOOD: { text: 'норма', cls: 'fc-good' },
  HIGH: { text: 'высоко', cls: 'fc-high' },
  CRITICAL: { text: 'в убыток', cls: 'fc-hot' },
  UNKNOWN: { text: 'нет цены', cls: '' },
};

/** Три знака после запятой — так весы и печатают. */
const kg = (v: number) => v.toLocaleString('ru-RU', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

async function call<T>(path: string, token: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    method: opts.method ?? 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<T>;
}

/** Шкала фудкоста: цифра, слово и полоса с порогами 35 / 45. */
function Foodcost({ pct, verdict }: { pct: number | null; verdict: Verdict }) {
  const v = VERDICT[verdict];
  return (
    <div className={`fc-cell ${v.cls}`}>
      <div className="fc-line">
        <b className="fc-num">{pct !== null ? `${pct} %` : '—'}</b>
        <span className="fc-word">{v.text}</span>
      </div>
      <div className="fc-track">
        <div className="fc-fill" style={{ width: `${Math.min(100, Math.max(2, pct ?? 0))}%` }} />
        <i className="fc-tick" style={{ left: '35%' }} />
        <i className="fc-tick" style={{ left: '45%' }} />
      </div>
    </div>
  );
}

export function CalcScreen({ token }: { token: string }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [onlyLoss, setOnlyLoss] = useState(false);

  const overview = useQuery({
    queryKey: ['calc-overview'],
    queryFn: () => call<{ rows: Row[]; withoutRecipe: number; critical: number }>('', token),
  });

  if (overview.isPending) return <p className="hint">Считаем калькуляции…</p>;
  const d = overview.data!;
  const rows = onlyLoss ? d.rows.filter((r) => r.verdict === 'CRITICAL') : d.rows;

  return (
    <section className="menu-cat calc-screen">
      <header><h2>Калькуляция</h2></header>

      <div className="calc-summary">
        <div className="sum-card">
          <span className="sum-label">Блюд с техкартой</span>
          <b className="sum-value">{d.rows.length}</b>
          <span className="sum-note">себестоимость считается по фактическим приходам</span>
        </div>

        <div className="sum-card loss">
          <span className="sum-label">Продаются в убыток</span>
          <div className="sum-line">
            <b className="sum-value">{d.critical}</b>
            <span className="sum-note">блюд · фудкост выше 45 %</span>
          </div>
          {d.critical > 0 && (
            <button className="sum-act" onClick={() => setOnlyLoss((v) => !v)}>
              {onlyLoss ? 'Показать все блюда' : 'Показать только их'}
            </button>
          )}
        </div>

        <div className="sum-card warn">
          <span className="sum-label">Без техкарты</span>
          <b className="sum-value">{d.withoutRecipe}</b>
          <span className="sum-note">их фудкост неизвестен</span>
        </div>
      </div>

      {d.rows.length === 0 ? (
        <p className="hint">
          Ни у одного блюда нет техкарты. Откройте любое блюдо ниже и задайте состав —
          система посчитает себестоимость по фактическим приходам и покажет фудкост.
        </p>
      ) : (
        // Грид с ролями таблицы: разметка перестраивается в карточки на 390,
        // а семантика строк и колонок при этом не теряется
        <div className="ctable" role="table" aria-label="Фудкост блюд">
          <div className="ctable-row head" role="row">
            <span role="columnheader">Блюдо</span>
            <span role="columnheader" className="num">Цена</span>
            <span role="columnheader" className="num">Себестоимость</span>
            <span role="columnheader" className="num">Наценка</span>
            <span role="columnheader">Фудкост · норма до 35 %</span>
            <span role="columnheader" aria-label="Действие" />
          </div>

          {rows.map((r) => (
            <div key={r.productId} className={`ctable-row ${r.verdict === 'CRITICAL' ? 'loss' : ''}`} role="row">
              <div className="ct-name" role="cell">
                <b>{r.productName}</b>
                {r.unpriced > 0 && (
                  <span className="calc-flag">Неполный расчёт · без цены позиций: {r.unpriced}</span>
                )}
              </div>
              <span className="num" data-money role="cell">{money(r.price)}</span>
              <span className="num" data-money role="cell">{money(r.cost)}</span>
              <span className="num ct-markup" role="cell">{r.markupPct !== null ? `${r.markupPct} %` : '—'}</span>
              <div className="ct-fc" role="cell"><Foodcost pct={r.foodcostPct} verdict={r.verdict} /></div>
              <span className="ct-act" role="cell">
                <button className="btn small" onClick={() => setOpenId(r.productId)}>Карта</button>
              </span>
            </div>
          ))}

          {rows.length === 0 && (
            <div className="ctable-row"><p className="hint">Убыточных блюд нет — это хорошая новость.</p></div>
          )}
        </div>
      )}

      {openId && <CardDialog token={token} productId={openId} onClose={() => setOpenId(null)} />}
    </section>
  );
}

function CardDialog({ token, productId, onClose }: {
  token: string; productId: string; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Card | null>(null);

  const { data } = useQuery({
    queryKey: ['calc-card', productId],
    queryFn: () => call<Card>(`/card?productId=${productId}`, token),
  });

  const card = draft ?? data;

  const save = useMutation({
    mutationFn: () => call('/card', token, {
      method: 'POST',
      body: {
        productId,
        yieldGram: card!.yieldGram,
        targetFoodcostPct: card!.targetFoodcostPct,
        lines: card!.lines.map((l) => ({
          ingredientId: l.ingredientId, gross: l.gross,
          coldLossPct: l.coldLossPct, hotLossPct: l.hotLossPct,
        })),
      },
    }),
    onSuccess: () => {
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['calc-card', productId] });
      void qc.invalidateQueries({ queryKey: ['calc-overview'] });
    },
  });

  if (!card) return null;

  const patch = (i: number, p: Partial<CardLine>) =>
    setDraft({ ...card, lines: card.lines.map((l, j) => (i === j ? { ...l, ...p } : l)) });

  return (
    <div className="dialog">
      <div className="dialog-card wide calc-card-dialog">
        <header className="calc-head">
          <div>
            <p className="eyebrow">Калькуляционная карта</p>
            <h2>{card.productName}</h2>
          </div>
          <div className={`cd-verdict ${VERDICT[card.verdict].cls}`}>
            <span>Фудкост</span>
            <div className="fc-line">
              <b className="fc-num">{card.foodcostPct !== null ? `${card.foodcostPct} %` : '—'}</b>
              <span className="fc-word">{VERDICT[card.verdict].text}</span>
            </div>
          </div>
        </header>

        {/* Три веса словами: без этой строки семь колонок ниже
            приходится держать в голове */}
        <div className="weights-legend">
          <div><b>Брутто · взяли со склада</b><span>за это заплачено поставщику — отсюда себестоимость</span></div>
          <div><b>Нетто · после чистки</b><span>кожура, плёнки, кости</span></div>
          <div><b>Выход · на тарелке</b><span>ужарка и уварка</span></div>
        </div>

        <div className="ing-table">
          <div className="ing-row head">
            <span>Ингредиент</span>
            <span className="num">Брутто</span>
            <span className="num">Потери хол., %</span>
            <span className="num">Потери гор., %</span>
            <span className="num">Нетто</span>
            <span className="num">Выход</span>
            <span className="num">Стоимость</span>
          </div>

          {card.lines.map((l, i) => {
            const keep = l.gross > 0 ? Math.max(2, Math.round((l.yield / l.gross) * 100)) : 0;
            const clean = l.gross > 0 ? Math.max(0, Math.round((l.net / l.gross) * 100) - keep) : 0;
            return (
              <div key={l.ingredientId} className="ing-line">
                <div className="ing-row">
                  <div className="ing-name">
                    <b>{l.name}</b>
                    <span>{l.ingredientUnit}</span>
                  </div>
                  <span className="num" data-l="Брутто">
                    <input type="number" step="0.001" value={l.gross} aria-label={`Брутто, ${l.name}`}
                      onChange={(e) => patch(i, { gross: Number(e.target.value) })} />
                  </span>
                  <span className="num" data-l="Потери холодные, %">
                    <input type="number" step="1" min={0} max={99} value={l.coldLossPct} aria-label={`Потери холодные, ${l.name}`}
                      onChange={(e) => patch(i, { coldLossPct: Number(e.target.value) })} />
                  </span>
                  <span className="num" data-l="Потери горячие, %">
                    <input type="number" step="1" min={0} max={99} value={l.hotLossPct} aria-label={`Потери горячие, ${l.name}`}
                      onChange={(e) => patch(i, { hotLossPct: Number(e.target.value) })} />
                  </span>
                  <span className="num ing-net" data-l="Нетто · после чистки">{kg(l.net)}</span>
                  <span className="num ing-yield" data-l="Выход · на тарелке">{kg(l.yield)}</span>
                  <span className="num ing-cost" data-l="Стоимость">
                    {l.cost !== null ? money(l.cost) : <i className="no-price">нет прихода</i>}
                  </span>
                </div>
                {/* Полоса убывания: латунь — то, что дошло до тарелки,
                    светлая часть — потери, за которые уже заплачено */}
                <div className="ing-flow">
                  <div className="flow-track">
                    <div className="flow-yield" style={{ width: `${keep}%` }} />
                    <div className="flow-loss" style={{ width: `${clean}%` }} />
                  </div>
                  <span className="flow-note">
                    взяли {kg(l.gross)} → после чистки {kg(l.net)} → на тарелке {kg(l.yield)}
                  </span>
                </div>
              </div>
            );
          })}

          {card.lines.length === 0 && (
            <p className="note">
              Состав не задан. Ингредиенты добавляются в разделе «Склад» → техкарта блюда.
            </p>
          )}

          <div className="ing-total">
            <b>Себестоимость порции</b>
            <span className="it-yield">выход <b>{kg(card.totalYield)}</b></span>
            <b className="it-sum" data-money>{money(card.cost)}</b>
          </div>
        </div>

        {card.unpriced > 0 && (
          <p className="rb-warn">
            У {card.unpriced} позиций нет прихода — себестоимость посчитана не полностью
            и <b>занижена</b>. Заведите поставку в разделе «Склад», и цифра станет честной.
          </p>
        )}

        <div className="calc-foot">
          <label>Выход порции, г
            <input type="number" value={card.yieldGram ?? ''}
              placeholder="не задан"
              onChange={(e) => setDraft({ ...card, yieldGram: e.target.value === '' ? null : Number(e.target.value) })} />
          </label>
          <label>Целевой фудкост, %
            <input type="number" min={5} max={90} value={card.targetFoodcostPct ?? ''}
              placeholder="напр. 30"
              onChange={(e) => setDraft({ ...card, targetFoodcostPct: e.target.value === '' ? null : Number(e.target.value) })} />
          </label>
          <div className="calc-price">
            <span>Цена в меню</span>
            <b data-money>{money(card.price)}</b>
          </div>
          {card.suggestedPrice !== null && card.suggestedPrice !== card.price && (
            <div className="calc-price suggest">
              <span>При целевом фудкосте {card.targetFoodcostPct ?? '—'} %</span>
              <b data-money>{money(card.suggestedPrice)}</b>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Закрыть</button>
          <button className="btn" onClick={() => window.print()}>Печать карты</button>
          <button className="btn accent" disabled={!draft || save.isPending}
            onClick={() => save.mutate()}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}
